import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Predictions from './pages/Predictions';
import DataSummary from './pages/DataSummary';
import StockCharts from './pages/StockCharts';
import Compare from './pages/Compare';
import ManageStocks from './pages/ManageStocks';
import MLPredictions from './pages/MLPredictions';
import Portfolio from './pages/Portfolio';
import AdminUsers from './pages/AdminUsers';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import EarningsCountdown from './components/EarningsCountdown';
import BackfillWidget from './components/BackfillWidget';
import { BackfillProvider } from './BackfillContext';
import { AuthProvider, useAuth } from './AuthContext';

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Don't show sidebar on auth pages
  const isAuthPage = ['/login', '/register'].includes(location.pathname);
  if (isAuthPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
      </Routes>
    );
  }

  const displayName = user?.fullName?.trim() || user?.email || 'User';
  const initials = user
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div className="app">
      {/* Mobile hamburger */}
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(o => !o)}
        aria-label="Toggle menu"
      >
        {sidebarOpen ? '✕' : '☰'}
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h1>Financial AI</h1>
          {user && (
            <div className="profile-menu-wrapper">
              <button
                className="profile-icon-btn"
                onClick={() => setProfileOpen(o => !o)}
                title={displayName}
              >
                {initials}
              </button>
              {profileOpen && (
                <div className="profile-dropdown">
                  <div className="profile-dropdown-name">{displayName}</div>
                  <div className="profile-dropdown-email">{user.email}</div>
                  <hr />
                  <NavLink to="/profile" onClick={() => { closeSidebar(); setProfileOpen(false); }}>Profile</NavLink>
                  <button onClick={() => { logout(); setProfileOpen(false); }}>Sign Out</button>
                </div>
              )}
            </div>
          )}
        </div>
        <nav>
          <NavLink to="/" end onClick={closeSidebar}>Analysis</NavLink>
          <NavLink to="/compare" onClick={closeSidebar}>Compare</NavLink>
          <NavLink to="/data" onClick={closeSidebar}>Data Summary</NavLink>
          <NavLink to="/charts" onClick={closeSidebar}>Stock Charts</NavLink>
          <NavLink to="/ml-predictions" onClick={closeSidebar}>Model Predictions</NavLink>
          <NavLink to="/portfolio" onClick={closeSidebar}>My Portfolio</NavLink>
          {user?.role === 'admin' && (
            <>
              <NavLink to="/manage" onClick={closeSidebar}>Manage Stocks</NavLink>
              <NavLink to="/admin/users" onClick={closeSidebar}>Manage Users</NavLink>
            </>
          )}
        </nav>
        <EarningsCountdown />
      </aside>
      <main className="main">
        <Routes>
          <Route path="/" element={<RequireAuth><Predictions /></RequireAuth>} />
          <Route path="/predictions/:symbol/:timeframe" element={<RequireAuth><Predictions /></RequireAuth>} />
          <Route path="/compare" element={<RequireAuth><Compare /></RequireAuth>} />
          <Route path="/compare/:symbols" element={<RequireAuth><Compare /></RequireAuth>} />
          <Route path="/data" element={<RequireAuth><DataSummary /></RequireAuth>} />
          <Route path="/data/:symbol/:period/:tab" element={<RequireAuth><DataSummary /></RequireAuth>} />
          <Route path="/charts" element={<RequireAuth><StockCharts /></RequireAuth>} />
          <Route path="/charts/stock/:symbol/view/:view" element={<RequireAuth><StockCharts /></RequireAuth>} />
          <Route path="/manage" element={<RequireAdmin><ManageStocks /></RequireAdmin>} />
          <Route path="/admin/users" element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
          <Route path="/ml-predictions" element={<RequireAuth><MLPredictions /></RequireAuth>} />
          <Route path="/portfolio" element={<RequireAuth><Portfolio /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
        </Routes>
      </main>
      <BackfillWidget />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BackfillProvider>
          <AppShell />
        </BackfillProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
